import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Paper,
  Tooltip,
  TextField,
  MenuItem,
  Pagination,
  Tabs,
  Tab,
  ButtonGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  Assignment,
  Visibility,
  Group,
  Person,
  CheckCircle,
  Info,
  Refresh,
  Download,
  Email,
  History,
  FilterList,
  Search,
  Clear,
  Send,
  ExpandMore,
  Business,
  Assessment,
  TableChart,
  PictureAsPdf,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface Sample {
  id: string;
  sample_id: string;
  external_id?: string; // Client reference
  client: {
    company_name: string;
  };
  type_echantillon: string;
  priorite: string;
  date_fin_analyse: string;
  metadonnees?: any;
}

interface Certificate {
  id: string;
  certificate_number: string;
  certificate_type: 'INDIVIDUAL' | 'BATCH';
  status: 'DRAFT' | 'APPROVED' | 'ISSUED';
  generated_at: string;
  sample: Sample;
  samples?: Sample[]; // For batch certificates
}

const CertificatesNew: React.FC = () => {
  const { user } = useAuth();
  
  // State
  const [readySamples, setReadySamples] = useState<Sample[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Dialog states
  const [generateDialog, setGenerateDialog] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState<Sample[]>([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  
  // New enhanced features state
  const [activeTab, setActiveTab] = useState(0); // 0: Generation, 1: History
  const [emailDialog, setEmailDialog] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: '',
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // History & Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCertificates, setTotalCertificates] = useState(0);
  
  // Generation Tab Pagination state
  const [generationCurrentPage, setGenerationCurrentPage] = useState(1);
  const [generationTotalPages, setGenerationTotalPages] = useState(1);
  const [totalReadySamples, setTotalReadySamples] = useState(0);
  const [generationPageSize] = useState(100);
  const [generationSearch, setGenerationSearch] = useState('');
  const [generationSearchInput, setGenerationSearchInput] = useState('');
  const [totalIssuedCertificates, setTotalIssuedCertificates] = useState(0);
  const [pageSize] = useState(10);
  
  // Filtering state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    dateFrom: '',
    dateTo: '',
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Batch visualization state
  const [selectedClientGroups, setSelectedClientGroups] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadReadySamples(),
        loadCertificates(),
        loadCertificateStats(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load data when filters or pagination change
  useEffect(() => {
    if (activeTab === 1) { // Only when on history tab
      loadCertificates();
    }
  }, [currentPage, filters, activeTab]);

  // Load data when generation tab pagination changes
  useEffect(() => {
    if (activeTab === 0) { // Only when on generation tab
      loadCertificates();
      loadReadySamples();
    }
  }, [currentPage, generationCurrentPage, generationSearch, activeTab]);

  const loadReadySamples = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for pagination and search
      const params = new URLSearchParams();
      params.append('page', generationCurrentPage.toString());
      params.append('limit', generationPageSize.toString());
      if (generationSearch) params.append('search', generationSearch);
      
      const url = `/api/v1/certificates/ready-samples?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReadySamples(data.data || []);
        
        // Update pagination info for generation tab
        if (data.pagination) {
          setGenerationTotalPages(data.pagination.totalPages || 1);
          setTotalReadySamples(data.pagination.total || 0);
        } else {
          // For basic mode, set simple pagination
          setGenerationTotalPages(1);
          setTotalReadySamples(data.data?.length || 0);
        }
      } else {
        throw new Error('Failed to load ready samples');
      }
    } catch (error) {
      console.error('Error loading ready samples:', error);
      throw error;
    }
  };

  const loadCertificates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for pagination and filtering
      const params = new URLSearchParams();
      
      if (activeTab === 1) { // History tab with pagination
        params.append('page', currentPage.toString());
        params.append('limit', pageSize.toString());
        
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        if (filters.type) params.append('type', filters.type);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
      }
      
      const url = `/api/v1/certificates${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCertificates(data.data || []);
        
        // Update pagination info if available
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages || 1);
          setTotalCertificates(data.pagination.total || 0);
        } else {
          // For basic mode, set simple pagination
          setTotalPages(1);
          setTotalCertificates(data.data?.length || 0);
        }
      } else {
        throw new Error('Failed to load certificates');
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
      throw error;
    }
  };

  const loadCertificateStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Load total certificates count
      const totalResponse = await fetch('/api/v1/certificates?limit=1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (totalResponse.ok) {
        const totalData = await totalResponse.json();
        if (totalData.pagination) {
          setTotalCertificates(totalData.pagination.total || 0);
        }
      }

      // Load issued certificates count
      const issuedResponse = await fetch('/api/v1/certificates?status=ISSUED&limit=1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (issuedResponse.ok) {
        const issuedData = await issuedResponse.json();
        if (issuedData.pagination) {
          setTotalIssuedCertificates(issuedData.pagination.total || 0);
        }
      }
    } catch (error) {
      console.error('Error loading certificate stats:', error);
      // Don't throw here, just log the error
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleGenerateIndividual = (sample: Sample) => {
    setSelectedSamples([sample]);
    setIsBatchMode(true); // Always batch API — correct lot layout even for 1 sample
    setGenerateDialog(true);
  };

  const handleGenerateBatch = () => {
    if (readySamples.length === 0) {
      showSnackbar('Aucun échantillon disponible pour certificat de lot', 'warning');
      return;
    }
    
    // Enhanced grouping by client with better sample organization
    const samplesByClient = readySamples.reduce((acc, sample) => {
      const clientName = sample.client?.company_name || 'Unknown';
      if (!acc[clientName]) {
        acc[clientName] = {
          clientName,
          samples: [],
          totalSamples: 0
        };
      }
      acc[clientName].samples.push(sample);
      acc[clientName].totalSamples += 1;
      return acc;
    }, {} as Record<string, { clientName: string; samples: Sample[]; totalSamples: number }>);

    // Find clients with multiple samples eligible for batch certificates
    const eligibleClients = Object.values(samplesByClient).filter(client => client.totalSamples > 1);
    
    if (eligibleClients.length === 0) {
      showSnackbar('Aucun client avec plusieurs échantillons pour certificat de lot', 'warning');
      return;
    }

    // If multiple clients are eligible, use the first one, or let user choose
    if (eligibleClients.length > 1) {
      // For now, use the client with the most samples
      const clientWithMostSamples = eligibleClients.reduce((prev, current) => 
        current.totalSamples > prev.totalSamples ? current : prev
      );
      
      showSnackbar(`Certificat de lot proposé pour ${clientWithMostSamples.clientName} (${clientWithMostSamples.totalSamples} échantillons)`, 'info');
      setSelectedSamples(clientWithMostSamples.samples);
    } else {
      // Only one eligible client
      const eligibleClient = eligibleClients[0];
      setSelectedSamples(eligibleClient.samples);
    }
    
    setIsBatchMode(true);
    setGenerateDialog(true);
  };

  const handleConfirmGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token');

      // Always use batch API (correct lot layout for 1+ samples)
      const response = await fetch('/api/v1/certificates/generate/batch', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sampleIds: selectedSamples.map(s => s.id),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        showSnackbar(`Certificat de lot généré: ${result.data.certificate_number}`, 'success');

        setTimeout(() => {
          window.open(`/api/v1/certificates/${result.data.id}/download`, '_blank');
        }, 1000);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erreur lors de la génération du certificat de lot');
      }

      setGenerateDialog(false);
      setSelectedSamples([]);
      await loadData(); // Reload data to refresh the lists
    } catch (error) {
      console.error('Error generating certificate:', error);
      showSnackbar((error as Error).message || 'Erreur lors de la génération du certificat', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewCertificate = (certificate: Certificate) => {
    const token = localStorage.getItem('access_token');
    
    // Create a form to POST the request with authentication
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = `/api/v1/certificates/${certificate.id}/download`;
    form.target = '_blank';
    
    // Add authorization header by using fetch and blob approach
    fetch(`/api/v1/certificates/${certificate.id}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération du certificat');
      }
      return response.text();
    })
    .then(htmlContent => {
      // Open the HTML content in a new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    })
    .catch(error => {
      console.error('Error viewing certificate:', error);
      showSnackbar('Erreur lors de la visualisation du certificat', 'error');
    });
  };

  // New enhanced functions
  const handleDownloadCertificate = (certificate: Certificate) => {
    const link = document.createElement('a');
    link.href = `/api/v1/certificates/${certificate.id}/download`;
    link.download = `Certificat-${certificate.certificate_number}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Téléchargement du certificat démarré', 'success');
  };

  const handleEmailCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setEmailData({
      to: certificate.sample?.client?.company_name ? '' : '',
      subject: `Certificat d'Analyse AFRILAB - ${certificate.certificate_number}`,
      message: `Bonjour,\n\nVeuillez trouver ci-joint le certificat d'analyse pour l'échantillon ${certificate.sample?.sample_id}.\n\nCordialement,\nLaboratoire AFRILAB`,
    });
    setEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!selectedCertificate || !emailData.to.trim()) {
      showSnackbar('Veuillez remplir l\'adresse email du destinataire', 'warning');
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/certificates/send-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          certificateId: selectedCertificate.id,
          to: emailData.to,
          subject: emailData.subject,
          message: emailData.message,
        }),
      });

      if (response.ok) {
        showSnackbar('Email envoyé avec succès', 'success');
        setEmailDialog(false);
        setSelectedCertificate(null);
        setEmailData({ to: '', subject: '', message: '' });
      } else {
        throw new Error('Erreur lors de l\'envoi de l\'email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showSnackbar('Erreur lors de l\'envoi de l\'email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  // New Export Functions
  const handleExportToExcel = async (certificate: Certificate) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch certificate data
      const response = await fetch(`/api/v1/certificates/${certificate.id}/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données du certificat');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
      
      // Generate proper Excel file with professional layout
      await generateProfessionalExcel(result.data, certificate.certificate_number);
      
      showSnackbar('Export Excel réussi', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showSnackbar('Erreur lors de l\'export Excel', 'error');
    }
  };

  const handleExportToPDF = async (certificate: Certificate) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // First, show loading message
      showSnackbar('Préparation du PDF en cours...', 'info');
      
      // Create a temporary PDF download link that forces download
      const link = document.createElement('a');
      link.href = `/api/v1/certificates/${certificate.id}/download`;
      link.download = `Certificat-${certificate.certificate_number}.html`;
      link.target = '_blank';
      
      // Add authorization header support by opening in new window with proper handling
      const pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        // Show loading message in the new window
        pdfWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>🏆 Téléchargement du Certificat Premium ${certificate.certificate_number}</title>
            <style>
              body {
                font-family: 'Segoe UI', Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background: linear-gradient(135deg, #F8F9FA 0%, #E7F3FF 100%);
              }
              .container {
                text-align: center;
                background: white;
                padding: 50px;
                border-radius: 20px;
                box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                max-width: 600px;
                border: 2px solid #E2E8F0;
              }
              .premium-header {
                color: #1F4E79;
                font-size: 24px;
                font-weight: 700;
                margin-bottom: 10px;
              }
              .spinner {
                border: 6px solid #f3f3f3;
                border-top: 6px solid #4A90D9;
                border-radius: 50%;
                width: 60px;
                height: 60px;
                animation: spin 1s linear infinite;
                margin: 30px auto;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .instructions {
                margin-top: 30px;
                text-align: left;
                background: linear-gradient(135deg, #E7F3FF 0%, #F0F8FF 100%);
                padding: 25px;
                border-radius: 15px;
                border: 2px solid #4A90D9;
                box-shadow: 0 4px 15px rgba(74, 144, 217, 0.1);
              }
              .instructions h4 {
                color: #1F4E79;
                margin-bottom: 15px;
                font-size: 18px;
              }
              .instructions ol {
                margin: 15px 0;
                color: #2F5597;
              }
              .instructions li {
                margin: 8px 0;
                font-weight: 500;
              }
              .button {
                background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
                color: white;
                border: none;
                padding: 15px 30px;
                border-radius: 25px;
                cursor: pointer;
                margin: 15px 10px;
                font-size: 16px;
                font-weight: 600;
                box-shadow: 0 4px 15px rgba(74, 144, 217, 0.3);
                transition: all 0.3s ease;
              }
              .button:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(74, 144, 217, 0.4);
              }
              .button.secondary {
                background: linear-gradient(135deg, #6B7280 0%, #4B5563 100%);
                box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3);
              }
              .loading-text {
                color: #2F5597;
                font-size: 18px;
                font-weight: 500;
                margin: 20px 0;
              }
              .premium-badge {
                display: inline-block;
                background: linear-gradient(135deg, #FFFBEB 0%, #FBBF24 100%);
                color: #0F2C47;
                padding: 8px 20px;
                border-radius: 20px;
                font-weight: 700;
                font-size: 12px;
                border: 2px solid #D97706;
                margin-bottom: 20px;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="premium-badge">🏆 AFRILAB PREMIUM REPORT</div>
              <div class="premium-header">✨ Certificat Premium ${certificate.certificate_number}</div>
              <div class="spinner"></div>
              <div class="loading-text">🔄 Préparation de votre certificat premium en cours...</div>
              <div class="instructions">
                <h4>📄 Pour sauvegarder en PDF Premium :</h4>
                <ol>
                  <li>Utilisez <strong>Ctrl+P</strong> (ou Cmd+P sur Mac)</li>
                  <li>Sélectionnez <strong>"Enregistrer au format PDF"</strong></li>
                  <li>Choisissez l'emplacement de sauvegarde</li>
                  <li>Admirez votre certificat au design premium ! ✨</li>
                </ol>
                <p><strong>💡 Avantages Premium :</strong> Design moderne, couleurs professionnelles, références automatiques sur chaque page, et styling corporate élégant.</p>
              </div>
              <button class="button" onclick="window.print()">🖨️ Imprimer/Sauvegarder PDF Premium</button>
              <button class="button secondary" onclick="window.close()">❌ Fermer</button>
            </div>
          </body>
          </html>
        `);
        
        // Load the actual certificate content
        fetch(`/api/v1/certificates/${certificate.id}/download`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).then(response => {
          if (!response.ok) {
            throw new Error('Erreur lors de la récupération du certificat');
          }
          return response.text();
        }).then(htmlContent => {
          // Replace the loading content with the actual certificate
          pdfWindow.document.open();
          pdfWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>🏆 Certificat ${certificate.certificate_number} - AFRILAB Premium Report</title>
              <style>
                @page {
                  size: A4;
                  margin: 20mm;
                }
                
                body {
                  font-family: 'Segoe UI', 'Arial', sans-serif;
                  line-height: 1.6;
                  color: #0F2C47;
                  margin: 0;
                  padding: 0;
                  background: #F8F9FA;
                }
                
                .pdf-header {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 50px;
                  background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
                  color: white;
                  padding: 15px 25px;
                  font-size: 14px;
                  font-weight: 600;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  z-index: 1000;
                  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                  border-bottom: 3px solid #FBBF24;
                }
                
                .pdf-header-item {
                  background: rgba(255,255,255,0.15);
                  padding: 8px 16px;
                  border-radius: 25px;
                  font-size: 13px;
                  font-weight: 500;
                  border: 1px solid rgba(255,255,255,0.3);
                  backdrop-filter: blur(10px);
                }
                
                .certificate-content {
                  margin-top: 100px;
                  padding: 30px;
                  background: white;
                  border-radius: 15px;
                  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
                  border: 2px solid #E2E8F0;
                }
                
                /* Premium Typography */
                h1, h2, h3 {
                  color: #1F4E79;
                  font-weight: 600;
                  margin-bottom: 20px;
                }
                
                h1 {
                  font-size: 28px;
                  text-align: center;
                  border-bottom: 3px solid #4A90D9;
                  padding-bottom: 15px;
                  margin-bottom: 30px;
                }
                
                h2 {
                  font-size: 22px;
                  color: #2F5597;
                  border-left: 5px solid #6BAA75;
                  padding-left: 15px;
                }
                
                /* Premium Tables */
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 25px 0;
                  font-size: 12px;
                  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                  border-radius: 10px;
                  overflow: hidden;
                }
                
                th {
                  background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
                  color: white;
                  font-weight: 600;
                  padding: 15px 12px;
                  text-align: center;
                  font-size: 11px;
                  border: none;
                }
                
                td {
                  padding: 12px;
                  text-align: center;
                  border-bottom: 1px solid #E1E5E9;
                  background: white;
                }
                
                tr:nth-child(even) td {
                  background: #F8F9FA;
                }
                
                tr:hover td {
                  background: #E7F3FF;
                  transition: background-color 0.3s ease;
                }
                
                /* Company Header Styling */
                .company-header {
                  text-align: center;
                  margin-bottom: 40px;
                  padding: 25px;
                  background: linear-gradient(135deg, #F8F9FA 0%, #ECEFF1 100%);
                  border-radius: 15px;
                  border: 2px solid #CBD5E0;
                }
                
                .company-name {
                  font-size: 24px;
                  font-weight: 700;
                  color: #0F2C47;
                  margin-bottom: 10px;
                }
                
                .report-title {
                  font-size: 18px;
                  color: #2F5597;
                  margin: 10px 0;
                  font-weight: 500;
                }
                
                .certificate-number {
                  display: inline-block;
                  background: linear-gradient(135deg, #FFFBEB 0%, #FBBF24 100%);
                  color: #0F2C47;
                  padding: 12px 24px;
                  border-radius: 25px;
                  font-weight: 700;
                  font-size: 14px;
                  border: 2px solid #D97706;
                  margin: 15px 0;
                  box-shadow: 0 4px 10px rgba(251, 191, 36, 0.3);
                }
                
                @media print {
                  .pdf-header {
                    position: fixed;
                    top: 0;
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                  }
                  .no-print {
                    display: none;
                  }
                  body {
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                    background: white;
                  }
                  .certificate-content {
                    box-shadow: none;
                    border: 1px solid #ddd;
                  }
                }
                
                @media screen {
                  .instructions-banner {
                    background: linear-gradient(135deg, #6BAA75 0%, #4A7C59 100%);
                    color: white;
                    padding: 20px;
                    text-align: center;
                    font-weight: 600;
                    margin-bottom: 25px;
                    border-radius: 10px;
                    font-size: 16px;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                    border: 2px solid #4A7C59;
                  }
                }
                
                /* Footer */
                .pdf-footer {
                  margin-top: 40px;
                  text-align: center;
                  color: #6B7280;
                  font-size: 11px;
                  padding: 20px;
                  border-top: 2px solid #E5E7EB;
                }
              </style>
            </head>
            <body>
              <div class="pdf-header">
                <div class="pdf-header-item">📋 Lab-Ref: ${certificate.sample?.sample_id || 'N/A'}</div>
                <div class="pdf-header-item">🏢 Client-Ref: ${certificate.sample?.external_id || 'N/A'}</div>
                <div class="pdf-header-item">🏆 ${certificate.certificate_number}</div>
              </div>
              
              <div class="instructions-banner no-print">
                🖨️ <strong>Appuyez sur Ctrl+P</strong> (Cmd+P sur Mac) puis sélectionnez <strong>"Enregistrer au format PDF"</strong> pour obtenir votre certificat premium
              </div>
              
              <div class="certificate-content">
                <div class="company-header">
                  <div class="company-name">✨ AFRILAB - African Laboratory for Mining and Environment ✨</div>
                  <div class="report-title">🔬 FORMULAIRE REALISATION 🔬</div>
                  <div class="report-title">📊 RAPPORT D'ANALYSE / ANALYTICAL REPORT 📈</div>
                  <div class="certificate-number">🏆 Certificat N° ${certificate.certificate_number} 🏆</div>
                </div>
                
                ${htmlContent}
                
                <div class="pdf-footer">
                  <p>✨ Ce document a été généré par AFRILAB - Laboratoire Africain pour l'Exploitation Minière et l'Environnement ✨</p>
                  <p>📅 Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
                  <p>🔒 Document confidentiel - Usage strictement professionnel</p>
                </div>
              </div>
              
              <script>
                // Auto-focus for immediate print dialog
                setTimeout(() => {
                  window.focus();
                }, 500);
                
                // Add premium styling to existing tables in the content
                document.addEventListener('DOMContentLoaded', function() {
                  const tables = document.querySelectorAll('table');
                  tables.forEach(table => {
                    table.style.borderRadius = '10px';
                    table.style.overflow = 'hidden';
                    table.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  });
                  
                  const headers = document.querySelectorAll('th');
                  headers.forEach(th => {
                    th.style.background = 'linear-gradient(135deg, #4A90D9 0%, #2E5984 100%)';
                    th.style.color = 'white';
                    th.style.fontWeight = '600';
                  });
                });
              </script>
            </body>
            </html>
          `);
          pdfWindow.document.close();
        }).catch(error => {
          console.error('Error loading certificate:', error);
          pdfWindow.document.open();
          pdfWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>🚨 Erreur - Certificat ${certificate.certificate_number}</title>
              <style>
                body {
                  font-family: 'Segoe UI', Arial, sans-serif;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 100vh;
                  margin: 0;
                  background: linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%);
                }
                .container {
                  text-align: center;
                  background: white;
                  padding: 50px;
                  border-radius: 20px;
                  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                  max-width: 500px;
                  border: 2px solid #FCA5A5;
                }
                .error-icon {
                  font-size: 60px;
                  margin-bottom: 20px;
                }
                .error-title {
                  color: #DC2626;
                  font-size: 24px;
                  font-weight: 700;
                  margin-bottom: 15px;
                }
                .error-message {
                  color: #7F1D1D;
                  font-size: 16px;
                  margin-bottom: 30px;
                  line-height: 1.5;
                }
                .button {
                  background: linear-gradient(135deg, #DC2626 0%, #B91C1C 100%);
                  color: white;
                  border: none;
                  padding: 15px 30px;
                  border-radius: 25px;
                  cursor: pointer;
                  font-size: 16px;
                  font-weight: 600;
                  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
                  transition: all 0.3s ease;
                }
                .button:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="error-icon">🚨</div>
                <div class="error-title">Erreur de Chargement</div>
                <div class="error-message">
                  Impossible de charger le certificat ${certificate.certificate_number}.<br>
                  Veuillez réessayer ou contacter le support technique.
                </div>
                <button class="button" onclick="window.close()">❌ Fermer la Fenêtre</button>
              </div>
            </body>
            </html>
          `);
          pdfWindow.document.close();
        });
      }
      
      showSnackbar('🏆 Fenêtre PDF Premium ouverte - Utilisez Ctrl+P pour sauvegarder votre certificat au design professionnel', 'success');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      showSnackbar('🚨 Erreur lors de l\'export PDF Premium - Veuillez réessayer', 'error');
    }
  };

  // Helper function to convert certificate data to CSV (REAL data like HTML table)
  const convertCertificateToCSV = (certificateData: any): string => {
    let csvContent = '';
    
    if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
      // BATCH CERTIFICATE - Table format like HTML
      const elements = certificateData.elements || [];
      
      // Header row: Lab-Ref, Client-Ref, then all elements
      const headers = ['Lab-Ref', 'Client-Ref', ...elements];
      csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
      
      // Data rows: each sample with its results
      certificateData.samples.forEach((sample: any) => {
        const row = [
          `"${sample.lab_ref}"`,
          `"${sample.client_ref}"`,
          ...elements.map((element: string) => {
            const result = sample.results?.find((r: any) => r.element === element);
            return result ? result.concentration : '0';
          })
        ];
        csvContent += row.join(',') + '\n';
      });
    } else {
      // INDIVIDUAL CERTIFICATE - Element list format
      const headers = ['Élément', 'Concentration', 'Unité'];
      csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
      
      const results = certificateData.results || [];
      results.forEach((result: any) => {
        const row = [
          `"${result.element}"`,
          `"${result.concentration}"`,
          `"${result.unit}"`
        ];
        csvContent += row.join(',') + '\n';
      });
    }
    
    return csvContent;
  };

  // Helper function to check if certificate has QA/QC data
  const hasQAQCData = (certificateData: any): boolean => {
    if (certificateData.certificate_type !== 'BATCH') return false;
    if (!certificateData.samples || certificateData.samples.length === 0) return false;
    
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    
    const isBGM = metadonnees?.bgm_method_selected === true;
    const qaqcData = metadonnees?.qaqc_data;
    const hasData = qaqcData?.reference_standards && 
                    Array.isArray(qaqcData.reference_standards) && 
                    qaqcData.reference_standards.length > 0;
    
    // 🔧 ZMSM/non-BGM support: gate on data presence only.
    // Mirror of the deployed surgical patch — backend already gates by data only too.
    void isBGM; // intentionally unused (kept for type-safety / readability)
    return hasData;
  };

  // Generate QA/QC worksheet for BGM certificates
/** Grey prefilled 2.5 kg when prep mécanique never recorded a real weight (non-BGM display). */

const MAIN_TABLE_PHYSICO_EXCEL = ['H2O'];

const resolveExcelMainSheetName = (certificateData: any): string => {
  const codes = new Set<string>();
  const addCode = (m: unknown) => {
    if (m != null && String(m).trim()) codes.add(String(m));
  };
  (certificateData.samples || []).forEach((sample: any) => {
    const meta = sample.sample_data?.metadonnees || sample.metadonnees || {};
    if (meta.bgm_method_selected) codes.add('BGM-MULTI-LAB');
    (meta.analyses_demandees || []).forEach(addCode);
    Object.values(meta.element_method_mapping || {}).forEach(addCode);
    (sample.results || []).forEach((r: any) => addCode(r.method_code));
  });
  (certificateData.results || []).forEach((r: any) => addCode(r.method_code));
  const list = Array.from(codes);
  if (list.some((m) => /BGM-MULTI-LAB/i.test(m))) return 'Rapport AA';
  if (list.some((m) => /ICP|D4A|FP-ICP|PRE\.MO\/ANA\/038|PRE\.MO\/ANA\/037|PRE\.MO\/ANA\/039|PRE\.MO\/ANA\/040|PRE\.MO\/ANA\/041|PRE\.MO\/ANA\/007/.test(m))) {
    return 'Rapport ICP';
  }
  if (list.some((m) => /ANA\/002|SAA|AA-PRE\.MO/i.test(m))) return 'Rapport SAA';
  return 'Rapport AA';
};

const excelAnalysisCellValue = (raw: unknown): string | number => {
  if (raw === null || raw === undefined) return "";
  return String(raw);
};

const elementIncludedInExcel = (sample: any, element: string, selected: string[]): boolean => {
  if (selected.includes(element)) return true;
  if ((sample.results ?? []).some((r: any) => r.element === element)) return true;
  const analysesDemandees = sample.sample_data?.metadonnees?.analyses_demandees || sample.metadonnees?.analyses_demandees || [];
  if (element === 'Au' && analysesDemandees.some((code: string) => code.includes('Au-PRE.MO/ANA'))) return true;
  if (!MAIN_TABLE_PHYSICO_EXCEL.includes(element)) return false;
  return false;
};


const getSampleRawWeightKgExcel = (sample: any): number | undefined => {
  const meta = sample.sample_data?.metadonnees || sample.metadonnees || {};
  const raw = meta.mechanical_preparation?.poids_apres_prep_kg ??
    meta.prep_mecanique?.poids_final_kg ??
    meta.poids_apres_prep_kg ??
    meta.poids_kg ??
    (meta.scale_weights?.preparation_mecanique?.weight_grams
      ? meta.scale_weights.preparation_mecanique.weight_grams / 1000
      : undefined) ??
    sample.weight;
  const n = parseFloat(String(raw ?? '').replace(',', '.'));
  return isFinite(n) ? n : undefined;
};

const shouldHideSampleWeightExcel = (sample: any): boolean => {
  const meta = sample.sample_data?.metadonnees || sample.metadonnees || {};
  if (meta.hide_weight_in_certificate) return true;
  return isPrefilledWeightKg(getSampleRawWeightKgExcel(sample), meta);
};

const isPrefilledWeightKg = (rawWeight: any, meta: any): boolean => {
  const n = parseFloat(String(rawWeight ?? '').replace(',', '.'));
  if (!isFinite(n) || n !== 2.5) return false;
  if (!meta || typeof meta !== 'object') return true;
  return !(
    meta.mechanical_preparation?.poids_apres_prep_kg ||
    meta.prep_mecanique?.poids_final_kg ||
    meta.poids_apres_prep_kg ||
    meta.scale_weights?.preparation_mecanique?.weight_grams
  );
};

const shortenMethodForExcel = (code: string): string => {
  const m = String(code || '').match(/PRE\.(?:MO|FO)\/[A-Z]+\/\d+(?:[-.][A-Z0-9]+)*/i);
  return m ? m[0] : (code || '-');
};


const formatQaqcLabWeightExcel = (raw: unknown): { value: string; hide: boolean } => {
  if (raw == null || String(raw).trim() === '') return { value: '', hide: true };
  const n = parseFloat(String(raw).replace(',', '.'));
  if (isFinite(n) && Math.abs(n - 2.5) < 0.001) return { value: '', hide: true };
  return { value: String(raw), hide: false };
};

const formatQaqcExcelCellValue = (unit: string, raw: unknown, element?: string): string => {
  let value = raw == null ? '' : String(raw);
  const sv = value.trim();
  if (sv !== '' && sv[0] !== '<' && unit !== '%') {
    const nf = parseFloat(sv.replace(',', '.'));
    if (!isNaN(nf)) { if (element === 'Au' || element === 'Au_FA' || unit === 'g/t') value = nf.toFixed(2); else value = String(Math.round(nf)); }
  }
  return value;
};

  const generateQAQCWorksheet = (workbook: any, certificateData: any, ExcelJS: any) => {
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    const qaqcData = metadonnees?.qaqc_data;
    
    if (!qaqcData?.reference_standards) return;
    
    const qaqcSheet = workbook.addWorksheet('QAQC');
    
    // 🔧 ZMSM/non-BGM support: when the cert is BGM, keep the fixed 10-element header
    //   (byte-for-byte unchanged). When non-BGM, derive columns from the actual
    //   reference_standards data so ZMSM (5 elems + optional Au) renders correctly.
    //   Mirror of the deployed surgical patch.
    const isBGMQAQC = (metadonnees as any)?.bgm_method_selected === true;
    const _refRow0: Record<string, any> = (qaqcData.reference_standards && qaqcData.reference_standards[0]) || {};
    const _defUnits: Record<string, string> = {
      Ag: 'g/t', Au: 'g/t', Au_FA: 'g/t', Ag_FA: 'g/t',
      Cu: '%', Fe: '%', Pb: '%', Zn: '%',
      Mo_ICP: 'ppm', As_ICP: 'ppm', Sn_ICP: 'ppm',
      Cd: 'ppm', Co: 'ppm', Mn: 'ppm', Ni: 'ppm', H2O: '%',
    };
    const _defMeth: Record<string, string> = {
      Ag: 'AA-PRE.MO/ANA/002', Au: 'AA-PRE.MO/ANA/001',
      Cu: 'AA-PRE.MO/ANA/002', Fe: 'AA-PRE.MO/ANA/002',
      Pb: 'AA-PRE.MO/ANA/002', Zn: 'AA-PRE.MO/ANA/002',
      Ag_FA: 'MO/ANA/035', Au_FA: 'MO/ANA/001',
      Mo_ICP: 'MO/ANA/038', As_ICP: 'MO/ANA/038', Sn_ICP: 'MO/ANA/038',
    };
    const _defLod: Record<string, string> = {
      Ag: '1', Au: '0,05', Cu: '0,01', Fe: '0,01', Pb: '0,01', Zn: '0,01',
      Ag_FA: '200', Au_FA: '0,05', Mo_ICP: '1', As_ICP: '1', Sn_ICP: '1',
    };
    const roleBased = qaqcData.role_based === true;
    const elementMeta: Record<string, { unit?: string; limit?: string; method?: string }> = qaqcData.element_meta || {};
    const oxideMajors = new Set(['SiO2', 'Al2O3', 'Fe2O3', 'CaO', 'MgO', 'Na2O', 'K2O', 'TiO2', 'MnO', 'P2O5', 'Cr2O3', 'V2O5']);
    const elSet = new Set<string>();
    Object.keys(elementMeta).forEach((k) => elSet.add(k));
    (qaqcData.reference_standards || []).forEach((std: any) => {
      Object.keys(std || {}).forEach((k) => {
        if (!['customers_ref', 'lab_ref', 'lab_weight'].includes(k)) elSet.add(k);
      });
    });
    const qaqcOrderBase: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
    const allElements: string[] = isBGMQAQC
      ? ['Ag', 'Cu', 'Fe', 'Pb', 'Zn', 'Ag_FA', 'Au_FA', 'Mo_ICP', 'As_ICP', 'Sn_ICP']
      : [
          ...qaqcOrderBase.filter((el) => elSet.has(el)),
          ...Array.from(elSet).filter((el) => !qaqcOrderBase.includes(el)),
        ];
    const elementUnits: Record<string, string> = isBGMQAQC
      ? {
          'Ag': 'g/t', 'Cu': '%', 'Fe': '%', 'Pb': '%', 'Zn': '%',
          'Ag_FA': 'g/t', 'Au_FA': 'g/t',
          'Mo_ICP': 'ppm', 'As_ICP': 'ppm', 'Sn_ICP': 'ppm'
        }
      : allElements.reduce((o: Record<string, string>, k) => {
          o[k] = elementMeta[k]?.unit || _defUnits[k] || (oxideMajors.has(k) ? '%' : 'ppm');
          return o;
        }, {});
    const methodHeaders: string[] = isBGMQAQC
      ? ['MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002',
         'MO/ANA/035', 'MO/ANA/001', 'MO/ANA/038', 'MO/ANA/038', 'MO/ANA/038']
      : allElements.map((k) => {
          if (roleBased && elementMeta[k]?.method) return shortenMethodForExcel(elementMeta[k].method!);
          return _defMeth[k] || '-';
        });
    const limitHeaders: string[] = isBGMQAQC
      ? ['1', '0,01', '0,01', '0,01', '0,01', '200', '0,05', '1', '1', '1']
      : allElements.map((k) => (roleBased && elementMeta[k]?.limit != null ? String(elementMeta[k].limit) : (_defLod[k] || '')));
    const refColCount = isBGMQAQC ? 2 : (roleBased ? 3 : 2);
    const elementColStart = refColCount + 1;
    const mergeEndCol = String.fromCharCode(64 + refColCount);
    
    const headerStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8CCE4' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: {
        top: { style: 'thin', color: { argb: 'FF555555' } },
        bottom: { style: 'thin', color: { argb: 'FF555555' } },
        left: { style: 'thin', color: { argb: 'FF555555' } },
        right: { style: 'thin', color: { argb: 'FF555555' } }
      }
    };
    
    const dataStyle = {
      font: { size: 10, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
      border: {
        top: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        left: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        right: { style: 'hair', color: { argb: 'FFDDDDDD' } }
      }
    };
    
    const emptyDataStyle = {
      ...dataStyle,
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }
    };
    
    qaqcSheet.mergeCells('A1:L1');
    qaqcSheet.getCell('A1').value = '2. QA/QC Results';
    qaqcSheet.getCell('A1').style = {
      font: { bold: true, size: 14, color: { argb: 'FF000000' }, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    qaqcSheet.getRow(1).height = 25;
    
    qaqcSheet.mergeCells(`A3:${mergeEndCol}3`);
    qaqcSheet.getCell('A3').value = 'METHOD';
    qaqcSheet.getCell('A3').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(3, idx + elementColStart);
      cell.value = methodHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(3).height = 25;
    
    qaqcSheet.mergeCells(`A4:${mergeEndCol}4`);
    qaqcSheet.getCell('A4').value = 'LIMITE DETECTION';
    qaqcSheet.getCell('A4').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(4, idx + elementColStart);
      cell.value = limitHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(4).height = 25;
    
    qaqcSheet.getCell('A5').value = 'Customers-Ref';
    qaqcSheet.getCell('A5').style = headerStyle;
    qaqcSheet.getCell('B5').value = 'Lab-Ref';
    qaqcSheet.getCell('B5').style = headerStyle;
    if (refColCount >= 3) {
      qaqcSheet.getCell('C5').value = 'LAB_WEIGHT\nKg';
      qaqcSheet.getCell('C5').style = headerStyle;
    }
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(5, idx + elementColStart);
      cell.value = `${el}\n${elementUnits[el]}`;
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(5).height = 30;
    
    qaqcData.reference_standards.forEach((standard: any, rowIdx: number) => {
      const rowNum = 6 + rowIdx;
      
      const custCell = qaqcSheet.getCell(rowNum, 1);
      custCell.value = standard.customers_ref || '';
      custCell.style = (!standard.customers_ref || standard.customers_ref.trim() === '') 
                       ? emptyDataStyle : dataStyle;
      
      const labCell = qaqcSheet.getCell(rowNum, 2);
      labCell.value = standard.lab_ref || '';
      labCell.style = (!standard.lab_ref || standard.lab_ref.trim() === '') 
                      ? emptyDataStyle : dataStyle;
      
      if (refColCount >= 3) {
        const weightCell = qaqcSheet.getCell(rowNum, 3);
        const { value: weightVal, hide: hideQaqcWeight } = formatQaqcLabWeightExcel(standard.lab_weight);
        weightCell.value = weightVal;
        weightCell.style = hideQaqcWeight ? emptyDataStyle : dataStyle;
      }
      allElements.forEach((el, colIdx) => {
        const cell = qaqcSheet.getCell(rowNum, colIdx + elementColStart);
        const raw = standard[el];
        const value = formatQaqcExcelCellValue(elementUnits[el] || 'ppm', raw, el);
        cell.value = value || '';
        cell.style = (!value || value.trim() === '') ? emptyDataStyle : dataStyle;
      });
      
      qaqcSheet.getRow(rowNum).height = 22;
    });
    
    const footerRow = 6 + qaqcData.reference_standards.length + 1;
    qaqcSheet.mergeCells(`A${footerRow}:L${footerRow}`);
    const footerCell = qaqcSheet.getCell(`A${footerRow}`);
    const analystName = qaqcData.batch_info?.analyst_name || 'N/A';
    const analysisDate = qaqcData.batch_info?.analysis_date 
      ? new Date(qaqcData.batch_info.analysis_date).toLocaleDateString('en-GB') 
      : 'N/A';
    footerCell.value = `* QA/QC data validated by: ${analystName} on ${analysisDate}`;
    footerCell.style = {
      font: { italic: true, size: 9, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    
    qaqcSheet.columns = [
      { width: 25 }, { width: 20 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }
    ];
  };

  // 🏆 PREMIUM CORPORATE EXCEL: Ultra-Modern Professional Design with Advanced Styling & Data Extraction
  const generateProfessionalExcel = async (certificateData: any, certificateNumber: string) => {
    try {
      // Load ExcelJS library with full styling support
      if (!(window as any).ExcelJS) {
        await loadExcelJSLibrary();
      }

      const ExcelJS = (window as any).ExcelJS;
      
      // Create workbook with ExcelJS (full styling support)
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(resolveExcelMainSheetName(certificateData));
      
      // ===== EXTRACT REAL CLIENT DATA FROM METADATA =====

      
      // Extract client info from batch metadata or samples
      let clientInfo = {
        client_name: 'AFRILAB CLIENT',
        project_name: 'GEOLOGICAL ANALYSIS', 
        client_reference: 'SAMPLE ANALYSIS',
        order_number: '',
        receipt_date: new Date().toLocaleDateString('en-GB')
      };
      
      // Try to extract from batch metadata
      if (certificateData.batch_info) {
        const metadata = certificateData.batch_info;
        clientInfo = {
          client_name: metadata.client_name || metadata.clientName || clientInfo.client_name,
          project_name: metadata.project_name || metadata.projectName || metadata.project || clientInfo.project_name,
          client_reference: metadata.client_reference || metadata.clientReference || metadata.reference || clientInfo.client_reference,
          order_number: metadata.order_number || metadata.orderNumber || metadata.order || clientInfo.order_number,
          receipt_date: metadata.receipt_date || metadata.receiptDate || metadata.date_reception || clientInfo.receipt_date
        };
      }
      
      // Try to extract from samples metadata  
      if (certificateData.samples && certificateData.samples.length > 0) {
        const firstSample = certificateData.samples[0];
        if (firstSample.metadata || firstSample.batch_info) {
          const sampleMeta = firstSample.metadata || firstSample.batch_info;
          clientInfo = {
            client_name: sampleMeta.client_name || sampleMeta.clientName || clientInfo.client_name,
            project_name: sampleMeta.project_name || sampleMeta.projectName || sampleMeta.project || clientInfo.project_name,
            client_reference: sampleMeta.client_reference || sampleMeta.clientReference || sampleMeta.reference || clientInfo.client_reference,
            order_number: sampleMeta.order_number || sampleMeta.orderNumber || sampleMeta.order || clientInfo.order_number,
            receipt_date: sampleMeta.receipt_date || sampleMeta.receiptDate || sampleMeta.date_reception || clientInfo.receipt_date
          };
        }
      }

      // Use client_name from API response (added by backend)
      if (certificateData.client_name) {
        clientInfo.client_name = certificateData.client_name;
      }
      if (certificateData.batch?.client_name) {
        clientInfo.client_name = certificateData.batch.client_name;
      }

      const isBGMClient =
        clientInfo.client_name === 'BGM' ||
        certificateData.batch?.client_name === 'BGM' ||
        certificateData.samples?.some((s: any) =>
          s.sample_data?.metadonnees?.bgm_method_selected === true ||
          s.metadonnees?.bgm_method_selected === true,
        );

      // Use real reception date from sample data
      if (certificateData.reception_date) {
        clientInfo.receipt_date = new Date(certificateData.reception_date).toLocaleDateString('en-GB');
      } else if (certificateData.samples?.[0]?.sample_data?.reception_date) {
        clientInfo.receipt_date = new Date(certificateData.samples[0].sample_data.reception_date).toLocaleDateString('en-GB');
      }


      // ===== PREMIUM CORPORATE STYLING (Based on 2024 Design Trends) =====
      
      const headerStyle = {
        font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A90D9' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF2E5984' } },
          bottom: { style: 'medium', color: { argb: 'FF2E5984' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E79' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const companyStyle = {
        font: { bold: true, size: 22, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'left', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const dataStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };
      
      const dataAlternateStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };

      const emptyDataStyle = {
        ...dataStyle,
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
      };
      
      const subtitleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const clientHeaderStyle = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6BAA75' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF4A7C59' } },
          bottom: { style: 'medium', color: { argb: 'FF4A7C59' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      // ===== BUILD BEAUTIFUL LAYOUT =====
      
      // Row 1: AFRILAB Company Header with Premium Corporate Style
      worksheet.mergeCells('A1:L1');
      const afrilab = worksheet.getCell('A1');
      afrilab.value = '✨ AFRILAB - African Laboratory for Mining and Environment ✨';
      afrilab.style = companyStyle;
      
      // Add subtle spacing row
      worksheet.getRow(2).height = 12;
      
      // Row 3: Report Form Title (moved down for better spacing)
      worksheet.mergeCells('D3:I3');
      const formTitle = worksheet.getCell('D3');
      formTitle.value = '🔬 FORMULAIRE REALISATION 🔬';
      formTitle.style = {
        font: { bold: true, size: 15, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F4F8' } },
        border: {
          top: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          bottom: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          left: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          right: { style: 'thin', color: { argb: 'FFCBD5E0' } }
        }
      };
      
      // Row 5-6: Report Titles (adjusted row numbers)
      worksheet.mergeCells('D5:I5');
      const reportTitle1 = worksheet.getCell('D5');
      reportTitle1.value = '📊 RAPPORT D\'ANALYSE 📊';
      reportTitle1.style = subtitleStyle;
      
      worksheet.mergeCells('D6:I6');
      const reportTitle2 = worksheet.getCell('D6');
      reportTitle2.value = '📈 ANALYTICAL REPORT 📈';
      reportTitle2.style = subtitleStyle;
      
      // Row 6: Certificate Number with Premium Design Box
      worksheet.mergeCells('J6:L6');
      const certCell = worksheet.getCell('J6');
      certCell.value = `🏆 Certificat N° ${certificateNumber} 🏆`;
      certCell.style = {
        font: { bold: true, size: 12, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFBBF24' } },
        border: {
          top: { style: 'thick', color: { argb: 'FFD97706' } },
          bottom: { style: 'thick', color: { argb: 'FFD97706' } },
          left: { style: 'thick', color: { argb: 'FFD97706' } },
          right: { style: 'thick', color: { argb: 'FFD97706' } }
        }
      };
      
      // Row 9-10: Report Info
      const dateCell = worksheet.getCell('A9');
      dateCell.value = 'Report issue date';
      dateCell.style = { font: { bold: true } };
      
      const dateCellValue = worksheet.getCell('C9');
      dateCellValue.value = clientInfo.receipt_date;
      
      const descCell = worksheet.getCell('A10');
      descCell.value = 'Please find below the results of analyzes.';
      
      // ===== CLIENT INFO TABLE WITH BEAUTIFUL HEADERS =====
      
      // Row 12: Headers with blue background
      const clientHeaders = [
        'Report number', 'Company', 'Project', 'Client reference', 'Order number',
        'Imputation code', 'date of receipt', 'Date of analysis', 'Sampled by',
        'Warehouse', 'Prepared by', 'CERTIFICAT'
      ];
      
      clientHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(12, i + 1);
        cell.value = header;
        cell.style = clientHeaderStyle;
      });
      
      // Row 13: Real Client Data
      const reportNumber = `AM/QA/DM/RA/${certificateNumber}/2025 V01`;
      const clientData = [
        reportNumber,
        clientInfo.client_name,
        clientInfo.project_name,
        clientInfo.client_reference,
        clientInfo.order_number,
        '',
        clientInfo.receipt_date,
        new Date().toLocaleDateString('en-GB'),
        'Customer',
        '',
        'AFRILAB',
        certificateNumber
      ];
      
      clientData.forEach((data, i) => {
        const cell = worksheet.getCell(13, i + 1);
        cell.value = data;
        cell.style = dataStyle;
      });
      
      // ===== ANALYSIS TABLE WITH PROFESSIONAL STYLING =====
      
      if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
        // Detect if this is a BGM batch
        const firstSample = certificateData.samples[0];
        const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
        const isBGMBatch = metadonnees?.bgm_method_selected === true;
        
        if (isBGMBatch) {
          // ===== BGM-SPECIFIC 25-COLUMN STRUCTURE =====
          
          // Define the fixed BGM column structure (25 columns: A-Y)
          const bgmColumns = [
            // Basic info (A-C)
            { key: 'Customers-Ref', header: 'Customers-Ref', method: '', limit: '', unit: '' },
            { key: 'Lab-Ref', header: 'Lab-Ref', method: '', limit: '', unit: '' },
            { key: 'LAB_WEIGHT', header: 'LAB_WEIGHT', method: '', limit: '', unit: 'Kg' },
            
            // SAA Elements (D-H) - HAS VALUES
            { key: 'Ag', header: 'Ag ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu', header: 'Cu', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe', header: 'Fe ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb', header: 'Pb ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn', header: 'Zn', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // SAA Check Elements (I-M) - EMPTY
            { key: 'Ag_CK', header: 'Ag_CK ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu_CK', header: 'Cu_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe_CK', header: 'Fe_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb_CK', header: 'Pb_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn_CK', header: 'Zn_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // Fire Assay Silver (N-O) - EMPTY
            { key: 'Ag_FA', header: 'Ag_FA', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            { key: 'Ag_FA_CK', header: 'Ag_FA_CK', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            
            // Fire Assay Gold (P) - HAS VALUES
            { key: 'Au', header: 'Au_FA', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Elements (Q-S) - HAS VALUES
            { key: 'Mo', header: 'Mo_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As', header: 'As_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn', header: 'Sn_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            
            // Gravimetric Gold (T-V) - EMPTY
            { key: 'Au_GRAV', header: 'Au_GRAV', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_GRAV_CK', header: 'Au_GRAV_CK', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_FA_CK', header: 'Au_FA_CK', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Check Elements (W-Y) - EMPTY
            { key: 'Mo_ICP_CK', header: 'Mo_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As_ICP_CK', header: 'As_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn_ICP_CK', header: 'Sn_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' }
          ];
          
          // Row 15: METHOD
          worksheet.mergeCells('A15:C15');
          const methodCell = worksheet.getCell('A15');
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= 3) { // Skip A-C (basic info columns)
              const cell = worksheet.getCell(15, i + 1);
              cell.value = col.method;
              cell.style = headerStyle;
            }
          });
          
          // Row 16: LIMITE DETECTION
          worksheet.mergeCells('A16:C16');
          const limitsCell = worksheet.getCell('A16');
          limitsCell.value = 'LIMITE DETECTION';
          limitsCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= 3) { // Skip A-C
              const cell = worksheet.getCell(16, i + 1);
              cell.value = col.limit;
              cell.style = headerStyle;
            }
          });
          
          // Row 17: Headers
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(17, i + 1);
            cell.value = col.header;
            cell.style = headerStyle;
          });
          
          // Row 18: Units
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(18, i + 1);
            cell.value = col.unit;
            cell.style = headerStyle;
          });
          
          // Sample Data Rows
          certificateData.samples.forEach((sample: any, index: number) => {
            const rowNum = 19 + index;
            const isEvenRow = index % 2 === 0;
            const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
            
            const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                   sample.metadonnees?.poids_kg || 
                                   sample.weight || 0.5;
            
            // Build row data according to BGM structure
            const sampleData: any[] = [];
            
            bgmColumns.forEach((col) => {
              if (col.key === 'Customers-Ref') {
                sampleData.push(sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`);
              } else if (col.key === 'Lab-Ref') {
                sampleData.push(sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`);
              } else if (col.key === 'LAB_WEIGHT') {
                sampleData.push(parseFloat(receptionWeight).toFixed(4));
              } else if (col.key === 'Ag') {
                // 🎯 BGM FEATURE: Ag superior limit - show ">200" when value exceeds 200 g/t
                const result = sample.results?.find((r: any) => r.element === 'Ag');
                if (result) {
                  const concentration = parseFloat(result.concentration || 0);
                  if (concentration > 200) {
                    sampleData.push('>200'); // Superior limit exceeded
                  } else {
                    const formatted = result.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key === 'Ag_FA') {
                // 🎯 BGM FEATURE: Ag_FA shows actual value ONLY when Ag > 200 (avoid redundancy)
                const agResult = sample.results?.find((r: any) => r.element === 'Ag');
                if (agResult) {
                  const concentration = parseFloat(agResult.concentration || 0);
                  if (concentration > 200) {
                    // Show actual Ag value (since Ag column shows ">200")
                    const formatted = agResult.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  } else {
                    // Empty cell (value already shown in Ag column)
                    sampleData.push('');
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key.endsWith('_CK') || col.key === 'Au_GRAV' || col.key === 'Au_GRAV_CK' || col.key === 'Au_FA_CK') {
                // Empty check columns
                sampleData.push('');
              } else {
                // Data columns: Cu, Fe, Pb, Zn, Au, Mo, As, Sn
                const result = sample.results?.find((r: any) => r.element === col.key);
                if (result) {
                  const formatted = result.formatted_concentration;
                  if (formatted !== undefined && formatted !== null) {
                    sampleData.push(String(formatted));
                  } else {
                    const concentration = parseFloat(result.concentration || 0);
                    sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                  }
                } else {
                  sampleData.push('');
                }
              }
            });
            
            worksheet.getRow(rowNum).height = 24;
            
            sampleData.forEach((value, i) => {
              const cell = worksheet.getCell(rowNum, i + 1);
              cell.value = value;
              if (typeof value === 'string' && value.startsWith('<')) {
                cell.numFmt = '@';
              }
              const isWeightCol = !shouldHideWeightColumn && i === 2;
              cell.style = (isWeightCol && (value === '' || value === null)) ? emptyDataStyle : currentDataStyle;
            });
          });
          
        } else {
          // ===== NON-BGM BATCHES: rows=samples, columns=elements (match HTML main table) =====
          const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
          const firstSample = certificateData.samples[0];
          const samplesArr = certificateData.samples || [];
          const shouldHideWeightColumn = !isBGMClient && samplesArr.every((s: any) => shouldHideSampleWeightExcel(s));
          const refColCount = shouldHideWeightColumn ? 2 : 3;
          const elementColStart = refColCount + 1;
          const mergeEndCol = String.fromCharCode(64 + refColCount);

          worksheet.mergeCells(`A15:${mergeEndCol}15`);
          const methodCell = worksheet.getCell('A15');
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;

          elements.forEach((elementName: string, i: number) => {
            const cell = worksheet.getCell(15, i + elementColStart);
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            cell.value = res?.method_code || '';
            cell.style = headerStyle;
          });

          worksheet.mergeCells(`A16:${mergeEndCol}16`);
          const limitsCell = worksheet.getCell('A16');
          limitsCell.value = 'LIMITE DETECTION';
          limitsCell.style = headerStyle;

          elements.forEach((elementName: string, i: number) => {
            const cell = worksheet.getCell(16, i + elementColStart);
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            const limit = res?.detection_limit;
            cell.value = (limit && typeof limit === 'object' && limit.limit != null) ? limit.limit : (typeof limit === 'number' ? limit : '');
            cell.style = headerStyle;
          });

          const headers = ['Customers-Ref', 'Lab-Ref'];
          if (!shouldHideWeightColumn) headers.push('LAB_WEIGHT');
          elements.forEach((element: string) => headers.push(element + '*'));
          headers.forEach((header, i) => {
            const cell = worksheet.getCell(17, i + 1);
            cell.value = header;
            cell.style = headerStyle;
          });

          const units = ['', ''];
          if (!shouldHideWeightColumn) units.push('Kg');
          elements.forEach((elementName: string) => {
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            units.push(res?.unit || '');
          });
          units.forEach((unit, i) => {
            const cell = worksheet.getCell(18, i + 1);
            cell.value = unit;
            cell.style = headerStyle;
          });

          samplesArr.forEach((sample: any, index: number) => {
            const rowNum = 19 + index;
            const isEvenRow = index % 2 === 0;
            const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
            const rawWeightKg = getSampleRawWeightKgExcel(sample);
            const hideSampleWeight = shouldHideSampleWeightExcel(sample);
            const sampleData: any[] = [
              sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`,
              sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`,
            ];
            if (!shouldHideWeightColumn) {
              sampleData.push(hideSampleWeight ? '' : (rawWeightKg != null ? rawWeightKg.toFixed(4) : ''));
            }
            const sampleSelectedElements = sample.sample_data?.metadonnees?.selected_elements ||
                                           sample.metadonnees?.selected_elements || [];
            elements.forEach((element: string) => {
              const result = sample.results?.find((r: any) => r.element === element);
              if (result) {
                const formatted = result.formatted_concentration;
                sampleData.push(excelAnalysisCellValue(formatted != null ? formatted : ''));
              } else if (elementIncludedInExcel(sample, element, sampleSelectedElements)) {
                sampleData.push('<DL');
              } else {
                sampleData.push('-');
              }
            });
            worksheet.getRow(rowNum).height = 24;
            sampleData.forEach((value, i) => {
              const cell = worksheet.getCell(rowNum, i + 1);
              cell.value = value;
              if (typeof value === 'string' && value.startsWith('<')) {
                cell.numFmt = '@';
              }
              cell.style = currentDataStyle;
            });
          });
        }
      } else if (certificateData.certificate_type === 'INDIVIDUAL') {
        const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
        const results: any[] = Array.isArray(certificateData.results) ? certificateData.results : [];

        // Method row
        worksheet.mergeCells('A15:C15');
        const methodCell = worksheet.getCell('A15');
        methodCell.value = 'METHOD';
        methodCell.style = headerStyle;
        elements.forEach((el, i) => {
          const cell = worksheet.getCell(15, i + 4);
          const res = results.find(r => r.element === el);
          cell.value = res?.method_code || '';
          cell.style = headerStyle;
        });

        // Detection limits row
        worksheet.mergeCells('A16:C16');
        const limitsCell = worksheet.getCell('A16');
        limitsCell.value = 'LIMITE DETECTION';
        limitsCell.style = headerStyle;
        elements.forEach((el, i) => {
          const cell = worksheet.getCell(16, i + 4);
          const res = results.find(r => r.element === el);
          const limit = res?.detection_limit;
          cell.value = typeof limit === 'number' ? limit : '';
          cell.style = headerStyle;
        });

        // Column headers
        const headers = ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT'];
        elements.forEach((el: string) => headers.push(el + '*'));
        headers.forEach((header, i) => {
          const cell = worksheet.getCell(17, i + 1);
          cell.value = header;
          cell.style = headerStyle;
        });

        // Units row
        const units = ['', '', 'Kg'];
        elements.forEach((el: string) => {
          const res = results.find(r => r.element === el);
          units.push(res?.unit || '');
        });
        units.forEach((unit, i) => {
          const cell = worksheet.getCell(18, i + 1);
          cell.value = unit;
          cell.style = headerStyle;
        });

        // Data row
        const rowNum = 19;
        worksheet.getRow(rowNum).height = 24;
        const sampleRow = [
          certificateData.client_ref || 'N/A',
          certificateData.lab_ref || 'N/A',
          (certificateData.sample_data?.metadonnees?.poids_kg || 0.5).toFixed(4),
        ];
        elements.forEach((el: string) => {
          const res = results.find(r => r.element === el);
          if (res) {
            const formatted = res.formatted_concentration;
            sampleRow.push(formatted != null ? String(formatted) : (Number.isFinite(+res.concentration) ? Number(res.concentration).toFixed(2) : 'N/A'));
          } else {
            sampleRow.push('<DL');
          }
        });
        sampleRow.forEach((value, i) => {
          const cell = worksheet.getCell(rowNum, i + 1);
          cell.value = value as any;
          if (typeof value === 'string' && value.startsWith('<')) {
            cell.numFmt = '@';
          }
          cell.style = dataStyle;
        });
      }
      
      // ===== PREMIUM SPACING & LAYOUT (Extra Wide & Tall) =====
      
      worksheet.columns = [
        { width: 25 }, // A: Customer-Ref (much wider for premium feel)
        { width: 22 }, // B: Lab-Ref (much wider for premium feel)
        { width: 15 }, // C: LAB_WEIGHT (wider)
        { width: 12 }, // D: Analysis columns (more spacious)
        { width: 12 }, // E: Analysis columns (more spacious)
        { width: 12 }, // F: Analysis columns (more spacious)
        { width: 12 }, // G: Analysis columns (more spacious)
        { width: 12 }, // H: Analysis columns (more spacious)
        { width: 12 }, // I: Analysis columns (more spacious)
        { width: 12 }, // J: Analysis columns (more spacious)
        { width: 12 }, // K: Analysis columns (more spacious)
        { width: 25 }  // L: Certificate column (much wider)
      ];
      
      // Set premium row heights with generous spacing
      worksheet.getRow(1).height = 45;  // AFRILAB header - extra prominent
      worksheet.getRow(2).height = 35;  // Form title - spacious
      worksheet.getRow(3).height = 20;  // More spacing
      worksheet.getRow(4).height = 30;  // Report titles - taller
      worksheet.getRow(5).height = 30;  // Report titles - taller
      worksheet.getRow(6).height = 28;  // Certificate number - premium height
      worksheet.getRow(7).height = 20;  // More spacing
      worksheet.getRow(8).height = 20;  // More spacing
      worksheet.getRow(9).height = 25;  // Date - taller
      worksheet.getRow(10).height = 25; // Description - taller
      worksheet.getRow(11).height = 20; // More spacing
      worksheet.getRow(12).height = 32; // Client headers - much taller
      worksheet.getRow(13).height = 28; // Client data - taller
      worksheet.getRow(14).height = 20; // More spacing
      worksheet.getRow(15).height = 30; // Method headers - taller
      worksheet.getRow(16).height = 30; // Detection limits - taller
      worksheet.getRow(17).height = 30; // Column headers - taller
      worksheet.getRow(18).height = 28; // Units - taller
      
      // ===== ADD QA/QC WORKSHEET IF DATA EXISTS (BGM CERTIFICATES ONLY) =====
      
      if (hasQAQCData(certificateData)) {
        generateQAQCWorksheet(workbook, certificateData, ExcelJS);
      }
      
      // ===== GENERATE AND DOWNLOAD PREMIUM CORPORATE EXCEL =====
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // BGM-specific filename: [LOT_NUM] [CLIENT] [FIRST_REF][LAST_3_CHARS].xlsx
      if (isBGMClient && certificateData.samples && certificateData.samples.length > 0) {
        const firstSampleId = certificateData.samples[0]?.sample_data?.sample_id || certificateData.samples[0]?.lab_ref || '';
        const lotMatch = firstSampleId.match(/^R-(\d+)/);
        const reportNum = lotMatch ? lotMatch[1] : (() => {
          const numParts = certificateNumber.split('/');
          const rawNum = (numParts[numParts.length - 1] || '').replace('-BATCH', '');
          return String(parseInt(rawNum, 10) || 0).padStart(4, '0');
        })();
        const firstExtId = (certificateData.samples[0]?.sample_data?.external_id || certificateData.samples[0]?.client_ref || '').trim();
        link.download = `${String(reportNum).padStart(4, '0')} ${clientInfo.client_name} ${firstExtId}.xlsx`;
      } else {
        link.download = `${certificateNumber}_AFRILAB_PREMIUM_CORPORATE_REPORT.xlsx`;
      }
      link.click();
      window.URL.revokeObjectURL(url);
      
      
      
    } catch (error) {
      console.error('Excel generation error:', error);
      throw new Error('Erreur lors de la génération du fichier Excel professionnel');
    }
  };

  // Load ExcelJS library for professional Excel generation with full styling
  const loadExcelJSLibrary = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).ExcelJS) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load ExcelJS library'));
      document.head.appendChild(script);
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      type: '',
      dateFrom: '',
      dateTo: '',
    });
    setCurrentPage(1);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'primary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ISSUED': return 'success';
      case 'APPROVED': return 'info';
      case 'DRAFT': return 'warning';
      default: return 'default';
    }
  };

  // Batch visualization helper functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    // Sort batches by batch number and individual samples by sample ID
    Object.keys(batches).forEach(batchNumber => {
      batches[batchNumber].sort((a, b) => {
        const posA = a.metadonnees?.batch_info?.sample_position || 0;
        const posB = b.metadonnees?.batch_info?.sample_position || 0;
        return posA - posB;
      });
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const handleBatchSelection = (batchNumber: string, isSelected: boolean) => {
    const newSelection = new Set(selectedClientGroups);
    if (isSelected) {
      newSelection.add(batchNumber);
    } else {
      newSelection.delete(batchNumber);
    }
    setSelectedClientGroups(newSelection);
  };

  const handleBatchExpansion = (batchNumber: string, isExpanded: boolean) => {
    const newExpanded = new Set(expandedGroups);
    if (isExpanded) {
      newExpanded.add(batchNumber);
    } else {
      newExpanded.delete(batchNumber);
    }
    setExpandedGroups(newExpanded);
  };

  const handleGenerateBatchFromLot = (batchNumber: string) => {
    const { batches } = groupSamplesByBatch(readySamples);
    const batchSamples = batches[batchNumber];
    
    if (!batchSamples || batchSamples.length === 0) {
      showSnackbar('Lot non trouvé', 'error');
      return;
    }

    // Always batch certificate (correct lot layout even for 1 sample)
    setSelectedSamples(batchSamples);
    setIsBatchMode(true);
    setGenerateDialog(true);
  };

  const handleGenerateBatchFromSelectedLots = () => {
    if (selectedClientGroups.size === 0) {
      showSnackbar('Veuillez sélectionner au moins un lot', 'warning');
      return;
    }

    // Get all samples from selected lots
    const { batches } = groupSamplesByBatch(readySamples);
    const selectedSamples = Array.from(selectedClientGroups)
      .filter(batchNumber => batches[batchNumber])
      .flatMap(batchNumber => batches[batchNumber]);

    if (selectedSamples.length === 0) {
      showSnackbar('Aucun échantillon trouvé dans les lots sélectionnés', 'warning');
      return;
    }

    setSelectedSamples(selectedSamples);
    setIsBatchMode(true);
    setGenerateDialog(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Gestion des Certificats
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="certificates tabs"
        >
          <Tab 
            icon={<Assignment />} 
            label="Génération" 
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab 
            icon={<History />} 
            label="Historique" 
            id="tab-1"
            aria-controls="tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Generation */}
      {activeTab === 0 && (
        <Box>
          {/* Statistics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Assignment color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalReadySamples}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prêts pour Certificat
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalCertificates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats Générés
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Info color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalIssuedCertificates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats Émis
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Ready Samples Section - Batch Visualization */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Échantillons Prêts pour Certificat ({readySamples.length} / {totalReadySamples})
                  </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      variant={viewMode === 'grouped' ? 'contained' : 'outlined'}
                      startIcon={<Business />}
                      onClick={() => setViewMode('grouped')}
                    >
                      Vue Groupée
                    </Button>
                    <Button
                      variant={viewMode === 'flat' ? 'contained' : 'outlined'}
                      startIcon={<Assessment />}
                      onClick={() => setViewMode('flat')}
                    >
                      Vue Liste
                    </Button>
                  </ButtonGroup>
                  <Button
                    variant="contained"
                    startIcon={<Group />}
                    onClick={handleGenerateBatchFromSelectedLots}
                    disabled={selectedClientGroups.size === 0}
                  >
                    Certificat de Lot ({selectedClientGroups.size} lots)
                  </Button>
                </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Rechercher par lot, client, référence..."
                    value={generationSearchInput}
                    onChange={(e) => setGenerationSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setGenerationSearch(generationSearchInput); setGenerationCurrentPage(1); } }}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => { setGenerationSearch(generationSearchInput); setGenerationCurrentPage(1); }}
                    startIcon={<Search />}
                  >
                    Rechercher
                  </Button>
                  {generationSearch && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => { setGenerationSearch(''); setGenerationSearchInput(''); setGenerationCurrentPage(1); }}
                      startIcon={<Clear />}
                    >
                      Effacer
                    </Button>
                  )}
                  {generationSearch && (
                    <Chip label={`Résultats pour "${generationSearch}"`} onDelete={() => { setGenerationSearch(''); setGenerationSearchInput(''); setGenerationCurrentPage(1); }} color="primary" size="small" />
                  )}
                </Box>
              </Box>

              {readySamples.length === 0 ? (
                <Alert severity="info">
                  Aucun échantillon prêt pour la génération de certificat.
                  Les échantillons doivent être validés avant la génération de certificat.
                </Alert>
              ) : (
                <>
                  {viewMode === 'grouped' ? (
                    // Grouped View - Lot-based accordions
                    <Box>
                      {(() => {
                        const { batches, individualSamples } = groupSamplesByBatch(readySamples);
                        const batchEntries = Object.entries(batches);
                        
                        return (
                          <>
                            {/* Batch Lots */}
                            {batchEntries.map(([batchNumber, batchSamples]) => (
                              <Accordion
                                key={batchNumber}
                                expanded={expandedGroups.has(batchNumber)}
                                onChange={(event, isExpanded) => 
                                  handleBatchExpansion(batchNumber, isExpanded)
                                }
                                sx={{ 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                                  border: '1px solid #2196f3',
                                  borderLeft: '6px solid #2196f3'
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedClientGroups.has(batchNumber)}
                                          onChange={(e) => handleBatchSelection(batchNumber, e.target.checked)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      }
                                      label=""
                                      sx={{ mr: 1 }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Assessment color="primary" />
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                      LOT {batchNumber}
                                    </Typography>
                                    <Badge 
                                      badgeContent={batchSamples.length} 
                                      color="primary"
                                      sx={{ mr: 2 }}
                                    >
                                      <Group />
                                    </Badge>
                                    <Chip 
                                      label={`${batchSamples.length} échantillons`}
                                      color="primary"
                                      size="small"
                                      sx={{ mr: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={batchSamples.length === 1 ? <Person /> : <Group />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGenerateBatchFromLot(batchNumber);
                                        }}
                                        disabled={batchSamples.length === 0}
                                      >
                                        {batchSamples.length === 1 ? 'Certificat' : 'Certificat Lot'}
                                      </Button>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Échantillon</TableCell>
                                          <TableCell>Position</TableCell>
                                          <TableCell>Référence Client</TableCell>
                                          <TableCell>Client</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Priorité</TableCell>
                                          <TableCell>Date Analyse</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {batchSamples.map((sample) => (
                                          <TableRow key={sample.id}>
                                            <TableCell>
                                              <Typography fontWeight="bold" variant="body2">
                                                {sample.sample_id}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={`${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                                color="info"
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" color="primary">
                                                {sample.external_id || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.client?.company_name || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.type_echantillon}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={sample.priorite}
                                                color={getPriorityColor(sample.priorite)}
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.date_fin_analyse 
                                                  ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                                  : 'N/A'
                                                }
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Tooltip title="Générer certificat individuel">
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  onClick={() => handleGenerateIndividual(sample)}
                                                >
                                                  <Person />
                                                </IconButton>
                                              </Tooltip>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                            
                            {/* Individual Samples */}
                            {individualSamples.length > 0 && (
                              <Accordion
                                expanded={expandedGroups.has('individual')}
                                onChange={(event, isExpanded) => 
                                  handleBatchExpansion('individual', isExpanded)
                                }
                                sx={{ 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
                                  border: '1px solid #4caf50',
                                  borderLeft: '6px solid #4caf50'
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Person color="success" />
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                      Échantillons Individuels
                                    </Typography>
                                    <Badge 
                                      badgeContent={individualSamples.length} 
                                      color="success"
                                      sx={{ mr: 2 }}
                                    >
                                      <Person />
                                    </Badge>
                                    <Chip 
                                      label={`${individualSamples.length} échantillons`}
                                      color="success"
                                      size="small"
                                    />
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Échantillon</TableCell>
                                          <TableCell>Référence Client</TableCell>
                                          <TableCell>Client</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Priorité</TableCell>
                                          <TableCell>Date Analyse</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {individualSamples.map((sample) => (
                                          <TableRow key={sample.id}>
                                            <TableCell>
                                              <Typography fontWeight="bold" variant="body2">
                                                {sample.sample_id}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" color="primary">
                                                {sample.external_id || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.client?.company_name || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.type_echantillon}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={sample.priorite}
                                                color={getPriorityColor(sample.priorite)}
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.date_fin_analyse 
                                                  ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                                  : 'N/A'
                                                }
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Tooltip title="Générer certificat individuel">
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  onClick={() => handleGenerateIndividual(sample)}
                                                >
                                                  <Person />
                                                </IconButton>
                                              </Tooltip>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  ) : (
                    // Flat View - Traditional table
                    <>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Échantillon</TableCell>
                              <TableCell>Référence Client</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Priorité</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {readySamples.map((sample) => (
                              <TableRow key={sample.id}>
                                <TableCell>
                                  <Typography fontWeight="bold">
                                    {sample.sample_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="primary">
                                    {sample.external_id || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>{sample.type_echantillon}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={sample.priorite}
                                    color={getPriorityColor(sample.priorite)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {sample.date_fin_analyse 
                                    ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                    : 'N/A'
                                  }
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Générer certificat individuel">
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleGenerateIndividual(sample)}
                                    >
                                      <Person />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination for Generation Tab */}
                      {generationTotalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination
                            count={generationTotalPages}
                            page={generationCurrentPage}
                            onChange={(_, page) => setGenerationCurrentPage(page)}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Generated Certificates Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Certificats Générés ({certificates.length} / {totalCertificates})
              </Typography>

              {certificates.length === 0 ? (
                <Alert severity="info">
                  Aucun certificat généré pour le moment.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Date Génération</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certificates.map((certificate) => (
                          <TableRow key={certificate.id}>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {certificate.certificate_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.certificate_type}
                                color={certificate.certificate_type === 'BATCH' ? 'secondary' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{certificate.sample?.sample_id || 'N/A'}</TableCell>
                            <TableCell>{certificate.sample?.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.status}
                                color={getStatusColor(certificate.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(certificate.generated_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Voir certificat">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleViewCertificate(certificate)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Pagination for Generated Certificates in Generation Tab */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab Panel 1: History */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Filtres de Recherche
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  Effacer
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Recherche"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Numéro, échantillon, client..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={filters.status}
                      label="Statut"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="DRAFT">Brouillon</MenuItem>
                      <MenuItem value="APPROVED">Approuvé</MenuItem>
                      <MenuItem value="ISSUED">Émis</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      label="Type"
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="INDIVIDUAL">Individuel</MenuItem>
                      <MenuItem value="BATCH">Lot</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Date de début"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Date de fin"
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* History Results */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Historique des Certificats ({totalCertificates})
                </Typography>
                {loading && <CircularProgress size={24} />}
              </Box>

              {certificates.length === 0 ? (
                <Alert severity="info">
                  Aucun certificat trouvé avec les critères actuels.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Date Génération</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certificates.map((certificate) => (
                          <TableRow key={certificate.id}>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {certificate.certificate_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.certificate_type}
                                color={certificate.certificate_type === 'BATCH' ? 'secondary' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{certificate.sample?.sample_id || 'N/A'}</TableCell>
                            <TableCell>{certificate.sample?.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.status}
                                color={getStatusColor(certificate.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(certificate.generated_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <ButtonGroup size="small" variant="outlined">
                                <Tooltip title="Voir certificat">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleViewCertificate(certificate)}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Télécharger HTML">
                                  <IconButton
                                    color="secondary"
                                    onClick={() => handleDownloadCertificate(certificate)}
                                  >
                                    <Download />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Export Excel">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleExportToExcel(certificate)}
                                  >
                                    <TableChart />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Export PDF">
                                  <IconButton
                                    color="warning"
                                    onClick={() => handleExportToPDF(certificate)}
                                  >
                                    <PictureAsPdf />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Envoyer par email">
                                  <IconButton
                                    color="info"
                                    onClick={() => handleEmailCertificate(certificate)}
                                  >
                                    <Email />
                                  </IconButton>
                                </Tooltip>
                              </ButtonGroup>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Generate Certificate Dialog */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isBatchMode ? <Group color="primary" /> : <Person color="primary" />}
            {isBatchMode ? 'Générer Certificat de Lot' : 'Générer Certificat Individuel'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {isBatchMode 
              ? `Vous allez générer un certificat de lot pour ${selectedSamples.length} échantillons:`
              : 'Vous allez générer un certificat individuel pour l\'échantillon:'
            }
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            {selectedSamples.map((sample) => (
              <Paper key={sample.id} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography fontWeight="bold">{sample.sample_id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {sample.type_echantillon}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="primary">
                      Réf. Client: {sample.external_id || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Client: {sample.client?.company_name || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          {isBatchMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Les certificats de lot ne peuvent être générés que pour des échantillons du même client.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateDialog(false)}
            disabled={generating}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGenerate}
            disabled={generating}
            startIcon={generating ? null : <Assignment />}
          >
            {generating ? 'Génération...' : 'Générer Certificat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Certificate Dialog */}
      <Dialog 
        open={emailDialog} 
        onClose={() => setEmailDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            Envoyer le Certificat par Email
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCertificate && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Certificat: <strong>{selectedCertificate.certificate_number}</strong><br />
                Échantillon: <strong>{selectedCertificate.sample?.sample_id}</strong><br />
                Client: <strong>{selectedCertificate.sample?.client?.company_name}</strong>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Destinataire *"
                    type="email"
                    value={emailData.to}
                    onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="client@example.com"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Objet"
                    value={emailData.subject}
                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={emailData.message}
                    onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Votre message personnalisé..."
                  />
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 2 }}>
                Le certificat sera joint au format HTML. Le destinataire pourra l'imprimer ou le sauvegarder en PDF.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialog(false)}
            disabled={sendingEmail}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={sendingEmail || !emailData.to.trim()}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <Send />}
          >
            {sendingEmail ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificatesNew; 